import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import userAPI from "../../utils/userAPI";

const ManagerManagement = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [currentManager, setCurrentManager] = useState({
    name: "",
    email: "",
    universityId: "",
  });
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadManagers();
  }, []);

  const loadManagers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userAPI.getAllManagers();
      setManagers(res.data || []);
    } catch (e) {
      setError("Failed to load managers");
    } finally {
      setLoading(false);
    }
  };

  const filtered = managers.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase()) ||
      (m.universityId || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = (mode, manager = null) => {
    setDialogMode(mode);
    setCurrentManager(
      manager ? { ...manager } : { name: "", email: "", universityId: "" }
    );
    setDialogOpen(true);
  };
  const handleChange = (e) =>
    setCurrentManager({ ...currentManager, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      if (dialogMode === "add") {
        const res = await userAPI.createManager(currentManager);
        setManagers([...managers, res.data.manager]);
        setSnackbar({
          open: true,
          message: "Manager created. Temp password copied from response.",
          severity: "success",
        });
      } else {
        const res = await userAPI.updateManager(
          currentManager.id,
          currentManager
        );
        setManagers(
          managers.map((m) => (m.id === currentManager.id ? res.data : m))
        );
        setSnackbar({
          open: true,
          message: "Manager updated.",
          severity: "success",
        });
      }
      setDialogOpen(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || "Failed to save manager",
        severity: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this manager?")) return;
    try {
      await userAPI.deleteManager(id);
      setManagers(managers.filter((m) => m.id !== id));
      setSnackbar({
        open: true,
        message: "Manager deleted",
        severity: "success",
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Failed to delete",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Manager Management
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Manage global maintenance managers (role: manager)
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search managers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => handleOpen("add")}
        >
          Add Manager
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>University ID</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>{m.universityId || "-"}</TableCell>
                  <TableCell>
                    {m.createdAt
                      ? new Date(m.createdAt).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("edit", m)}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(m.id)}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" align="center">
                      No managers found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogMode === "add" ? "Add Manager" : "Edit Manager"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={currentManager.name}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            value={currentManager.email}
            onChange={handleChange}
            disabled={dialogMode === "edit"}
          />
          <TextField
            fullWidth
            margin="normal"
            label="University ID"
            name="universityId"
            value={currentManager.universityId}
            onChange={handleChange}
          />
          {dialogMode === "add" && (
            <Alert severity="info">
              After creation, user must log in then refresh token to receive
              manager privileges.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialogMode === "add" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagerManagement;
